/* ============================================================
   Modulo: Timer Marquee — seamless scroll + live countdown.

   Two jobs, one file:
   1) SCROLL — clone-to-fill engine (mirrors section-marquee):
      measure one cycle, clone the set until track ≥ viewport +
      1 cycle, set --modulo-mc-cycle-width, mark ready=1 so the CSS
      animation attaches with no jumpy first paint. Skipped under
      prefers-reduced-motion (CSS wraps + centers instead).
   2) TIMERS — one shared 1s tick updates every [data-modulo-mc-timer]
      (originals AND clones, re-queried each tick so resize-added
      clones stay in sync). Runs regardless of reduced motion, so a
      non-scrolling band still counts down. Freezes at 0 on end.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Scroll ─── */
  function getGap(el) {
    var style = getComputedStyle(el);
    var n = parseFloat(style.columnGap || style.gap || '0');
    return isNaN(n) ? 0 : n;
  }

  function measureCycle(items, gap) {
    var w = 0;
    for (var i = 0; i < items.length; i++) {
      w += items[i].getBoundingClientRect().width;
    }
    return w + gap * items.length;
  }

  // A clone is a decorative duplicate: hide it from the a11y tree AND pull every
  // focusable descendant (button/image/word links this section can scroll) out of
  // the tab order, so an aria-hidden anchor never becomes a phantom keyboard stop.
  function deactivateClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('role');
    if (clone.matches && clone.matches('a[href], button, [tabindex]')) {
      clone.setAttribute('tabindex', '-1');
    }
    if (clone.querySelectorAll) {
      var focusables = clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
      for (var f = 0; f < focusables.length; f++) focusables[f].setAttribute('tabindex', '-1');
    }
  }

  function cloneSet(track, items) {
    for (var i = 0; i < items.length; i++) {
      var clone = items[i].cloneNode(true);
      deactivateClone(clone);
      track.appendChild(clone);
    }
  }

  function initScroll(row) {
    if (REDUCE) return;
    var track = row.querySelector('.modulo-mc__track');
    if (!track || track.dataset.moduloMcReady === '1') return;

    var items = Array.prototype.slice.call(track.children);
    if (items.length === 0) return;

    var gap = getGap(track);
    var cycle = measureCycle(items, gap);
    if (cycle <= 0) return; // layout not ready

    var viewport = row.offsetWidth || document.documentElement.clientWidth;
    var copiesNeeded = Math.max(2, Math.ceil((viewport + cycle) / cycle));
    for (var c = 0; c < copiesNeeded; c++) cloneSet(track, items);

    track.style.setProperty('--modulo-mc-cycle-width', cycle + 'px');
    track.dataset.moduloMcReady = '1';
  }

  /* ─── Timers ─── */
  function pad2(n) {
    n = Math.max(0, Math.floor(n));
    return n < 10 ? '0' + n : String(n);
  }

  function renderTimer(el) {
    var endAttr = el.getAttribute('data-end-ms');
    var end = endAttr ? parseInt(endAttr, 10) : 0;
    if (!end) return;
    var diff = Math.max(0, end - Date.now());
    var s = Math.floor(diff / 1000);
    var days = Math.floor(s / 86400);
    var hrs  = Math.floor((s % 86400) / 3600);
    var mins = Math.floor((s % 3600) / 60);
    var secs = s % 60;
    var d = el.querySelector('[data-unit="days"]');
    var h = el.querySelector('[data-unit="hours"]');
    var m = el.querySelector('[data-unit="minutes"]');
    var sc = el.querySelector('[data-unit="seconds"]');
    if (d)  d.textContent  = String(days);
    if (h)  h.textContent  = pad2(hrs);
    if (m)  m.textContent  = pad2(mins);
    if (sc) sc.textContent = pad2(secs);
    // Keep the role="img" accessible name in sync with the visible digits so a
    // screen-reader user never hears a stale load-time snapshot. No aria-live —
    // this only refreshes the name, it does not announce on every tick.
    var ld = el.getAttribute('data-l-days')    || 'days';
    var lh = el.getAttribute('data-l-hours')   || 'hours';
    var lm = el.getAttribute('data-l-minutes') || 'minutes';
    var ls = el.getAttribute('data-l-seconds') || 'seconds';
    el.setAttribute('aria-label', 'Time remaining: ' + days + ' ' + ld + ', ' + pad2(hrs) + ' ' + lh + ', ' + pad2(mins) + ' ' + lm + ', ' + pad2(secs) + ' ' + ls);
  }

  function tickAll() {
    var timers = document.querySelectorAll('[data-modulo-mc-timer]');
    for (var i = 0; i < timers.length; i++) renderTimer(timers[i]);
  }

  var timerInterval = null;
  function startTimers() {
    if (!document.querySelector('[data-modulo-mc-timer]')) return;
    tickAll();
    if (!timerInterval) timerInterval = setInterval(tickAll, 1000);
  }

  /* ─── Boot ─── */
  function initRows(scope) {
    var rows = (scope || document).querySelectorAll('[data-modulo-mc]');
    for (var i = 0; i < rows.length; i++) initScroll(rows[i]);
  }

  function boot() {
    if (window.moduloLazyInit) {
      window.moduloLazyInit('[data-modulo-mc]', initScroll);
    } else {
      initRows(document);
    }
    startTimers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-fill on resize (only ever ADD clones — cheap and safe).
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (REDUCE) return;
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var rows = document.querySelectorAll('[data-modulo-mc]');
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var track = row.querySelector('.modulo-mc__track');
        if (!track || track.dataset.moduloMcReady !== '1') continue;
        var needed = row.offsetWidth * 2;
        var safety = 0;
        while (track.scrollWidth < needed && safety < 4) {
          var snapshot = Array.prototype.slice.call(track.children);
          for (var j = 0; j < snapshot.length; j++) {
            var clone = snapshot[j].cloneNode(true);
            deactivateClone(clone);
            track.appendChild(clone);
          }
          safety++;
        }
      }
    }, 200);
  });

  // Theme editor: re-init a section when it is (re)rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initRows(e.target);
    startTimers();
  });
})();
